import React from "react";
import { EditableInput } from "../../../styles/SettingsPage.styled";
import { Dropdown } from "antd";
import { tableTitleStyle } from "../../MarketOverviewPage/data/constants";
import { NameCompletionRowData, NameCompletionTableProps } from "../types";
import { validateGroupSums } from "../../../hooks/SettingsPage/nameCompletionHandlers";

const RESTAURANT_COLOR = "#16a34a";
const GROCERY_COLOR = "#d97706";

interface GetColumnsParams {
  isEditing: boolean;
  editingData: { [key: string]: Partial<NameCompletionRowData> };
  handleInputChange: NameCompletionTableProps["handleInputChange"];
  handleSaveDefaultValue: NameCompletionTableProps["handleSaveDefaultValue"];
  handleApplyDefaultToAll: NameCompletionTableProps["handleApplyDefaultToAll"];
  handleApplyDefaultToEditingRow: NameCompletionTableProps["handleApplyDefaultToEditingRow"];
  handleApplyDefaultToRow: NameCompletionTableProps["handleApplyDefaultToRow"];
  setIsApplyDefaultConfirmOpen: (open: boolean) => void;
  setPendingApplyDefaultKey: (key: string | null) => void;
  setIsApplyToAllConfirmOpen: (open: boolean) => void;
}

const weightCell = (
  dataIndex: keyof NameCompletionRowData,
  record: NameCompletionRowData,
  isEditing: boolean,
  editingData: { [key: string]: Partial<NameCompletionRowData> },
  handleInputChange: GetColumnsParams["handleInputChange"]
) => {
  const value = (record[dataIndex] as number) ?? 0;
  const isOverrideRow = !record.is_default && record.key !== "default-value";
  const isDefaultRow = record.key === "default-value";
  if (isEditing) {
    const editedValue = editingData[record.key]?.[dataIndex] as number | undefined;
    return (
      <div className="flex items-center justify-center">
        <EditableInput
          size="small"
          min={0}
          max={100}
          value={editedValue ?? value}
          suffix="%"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const num = parseFloat(e.target.value) || 0;
            handleInputChange(record.key, dataIndex, num);
          }}
        />
      </div>
    );
  }
  return (
    <div
      className="text-center text-xs font-normal px-2.5 py-1 rounded-sm inline-flex justify-center items-center"
      style={isOverrideRow || isDefaultRow ? { fontWeight: 500 } : { fontStyle: "italic" }}
    >
      {value}%
    </div>
  );
};

const sumCell = (
  record: NameCompletionRowData,
  group: "restaurant" | "grocery",
  editingData: { [key: string]: Partial<NameCompletionRowData> },
  isEditing: boolean
) => {
  if (!isEditing) return null;
  const merged = { ...record, ...(editingData[record.key] ?? {}) };
  const { restaurantSum, grocerySum, restaurantValid, groceryValid } = validateGroupSums(merged);
  const sum = group === "restaurant" ? restaurantSum : grocerySum;
  const valid = group === "restaurant" ? restaurantValid : groceryValid;
  const rounded = Math.round(sum * 100) / 100;
  return (
    <div
      className="text-center text-xs font-medium mt-0.5"
      style={{ color: valid ? "#16a34a" : "#dc2626" }}
    >
      {rounded}%
    </div>
  );
};

export const getNameCompletionColumns = ({
  isEditing,
  editingData,
  handleInputChange,
  handleSaveDefaultValue,
  handleApplyDefaultToEditingRow,
  setIsApplyToAllConfirmOpen,
}: GetColumnsParams): any[] => [
  {
    title: <div className="flex items-center gap-1 justify-left px-2">Market</div>,
    dataIndex: "market",
    key: "market",
    width: 110,
    render: (text: string, record: NameCompletionRowData) => {
      const isOverride = !record.is_default && record.key !== "default-value";
      const isDefaultRow = record.key === "default-value";
      return (
        <div className="flex flex-col items-start px-1">
          <span className={`text-[0.75rem] text-left ${isDefaultRow || isOverride ? "font-medium" : ""}`}>
            {text}
          </span>
          {isOverride && (
            <span className="text-[0.625rem] text-[#0e49b5] text-left">Override Value</span>
          )}
        </div>
      );
    },
  },
  {
    title: "Restaurant Weights",
    children: [
      {
        title: <div className="flex justify-center items-center text-[0.75rem]">Subbrand</div>,
        dataIndex: "subbrand_restaurant",
        key: "subbrand_restaurant",
        width: 88,
        render: (_: any, record: NameCompletionRowData) =>
          weightCell("subbrand_restaurant", record, isEditing, editingData, handleInputChange),
      },
      {
        title: <div className="flex justify-center items-center text-[0.75rem]">Volume</div>,
        dataIndex: "volume_restaurant",
        key: "volume_restaurant",
        width: 80,
        render: (_: any, record: NameCompletionRowData) =>
          weightCell("volume_restaurant", record, isEditing, editingData, handleInputChange),
      },
      {
        title: <div className="flex justify-center items-center text-[0.75rem] whitespace-nowrap">Packaging Type</div>,
        dataIndex: "packaging_type_restaurant",
        key: "packaging_type_restaurant",
        width: 105,
        render: (_: any, record: NameCompletionRowData) =>
          weightCell("packaging_type_restaurant", record, isEditing, editingData, handleInputChange),
      },
      {
        title: <div className="flex justify-center items-center text-[0.75rem] whitespace-nowrap">Packaging Size</div>,
        dataIndex: "packaging_size_restaurant",
        key: "packaging_size_restaurant",
        width: 105,
        render: (_: any, record: NameCompletionRowData) => (
          <div className="flex flex-col items-center">
            {weightCell("packaging_size_restaurant", record, isEditing, editingData, handleInputChange)}
            {sumCell(record, "restaurant", editingData, isEditing)}
          </div>
        ),
      },
    ],
    onHeaderCell: () => ({
      style: {
        ...tableTitleStyle,
        backgroundColor: RESTAURANT_COLOR,
        borderTopLeftRadius: "12px",
      },
    }),
  },
  {
    title: "Grocery Weights",
    children: [
      {
        title: <div className="flex justify-center items-center text-[0.75rem]">Subbrand</div>,
        dataIndex: "subbrand_grocery",
        key: "subbrand_grocery",
        width: 88,
        render: (_: any, record: NameCompletionRowData) =>
          weightCell("subbrand_grocery", record, isEditing, editingData, handleInputChange),
      },
      {
        title: <div className="flex justify-center items-center text-[0.75rem]">Volume</div>,
        dataIndex: "volume_grocery",
        key: "volume_grocery",
        width: 80,
        render: (_: any, record: NameCompletionRowData) =>
          weightCell("volume_grocery", record, isEditing, editingData, handleInputChange),
      },
      {
        title: <div className="flex justify-center items-center text-[0.75rem] whitespace-nowrap">Packaging Type</div>,
        dataIndex: "packaging_type_grocery",
        key: "packaging_type_grocery",
        width: 105,
        render: (_: any, record: NameCompletionRowData) =>
          weightCell("packaging_type_grocery", record, isEditing, editingData, handleInputChange),
      },
      {
        title: <div className="flex justify-center items-center text-[0.75rem] whitespace-nowrap">Packaging Size</div>,
        dataIndex: "packaging_size_grocery",
        key: "packaging_size_grocery",
        width: 105,
        render: (_: any, record: NameCompletionRowData) => (
          <div className="flex flex-col items-center">
            {weightCell("packaging_size_grocery", record, isEditing, editingData, handleInputChange)}
            {sumCell(record, "grocery", editingData, isEditing)}
          </div>
        ),
      },
    ],
    onHeaderCell: () => ({
      style: {
        ...tableTitleStyle,
        backgroundColor: GROCERY_COLOR,
        borderTopRightRadius: "12px",
      },
    }),
  },
  {
    title: <div className="flex items-center gap-1 justify-center px-3">Edit by</div>,
    dataIndex: "editBy",
    key: "editBy",
    width: 140,
    render: (_: any, record: NameCompletionRowData) => {
      const text = record.editBy ?? "";
      const date = record.updated_at
        ? new Date(record.updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        : null;
      return (
        <div className="flex items-center">
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <div
                className="text-white flex items-center justify-center"
                style={{ width: "20px", height: "20px", borderRadius: "50%", backgroundColor: RESTAURANT_COLOR }}
              >
                {text ? text.charAt(0).toUpperCase() : "D"}
              </div>
              <span className="text-[0.75rem] text-left">{text || "-"}</span>
            </div>
            {date && (
              <div className="h-4 text-[0.5rem] text-gray-500 text-left flex items-center">
                Last Edit on {date}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    title: "",
    key: "actions",
    width: 40,
    render: (_: any, record: NameCompletionRowData) => {
      if (!isEditing) return null;
      const isDefaultRow = record.key === "default-value";
      if (isDefaultRow) {
        return (
          <div className="flex items-center justify-center">
            <Dropdown
              menu={{
                items: [
                  {
                    key: "save-default",
                    label: "Save Default",
                    onClick: () => handleSaveDefaultValue(record.key),
                  },
                  {
                    key: "apply-to-all",
                    label: "Apply to All",
                    onClick: () => setIsApplyToAllConfirmOpen(true),
                  },
                ],
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <button
                className="text-blue-600 hover:text-blue-400 transition-colors duration-200 bg-transparent border-none cursor-pointer p-1 rounded flex items-center justify-center"
                style={{ width: "24px", height: "24px" }}
                aria-label="Default value options"
              >
                <div className="flex items-center justify-center gap-0.5">
                  <div className="w-1 h-1 bg-current rounded-full"></div>
                  <div className="w-1 h-1 bg-current rounded-full"></div>
                  <div className="w-1 h-1 bg-current rounded-full"></div>
                </div>
              </button>
            </Dropdown>
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center">
          <Dropdown
            menu={{
              items: [
                {
                  key: "apply-default",
                  label: "Apply default value here",
                  onClick: () => handleApplyDefaultToEditingRow(record.key),
                },
              ],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <button
              className="text-blue-600 hover:text-blue-400 transition-colors duration-200 bg-transparent border-none cursor-pointer p-1 rounded flex items-center justify-center"
              style={{ width: "24px", height: "24px" }}
              aria-label="More actions"
            >
              <div className="flex items-center justify-center gap-0.5">
                <div className="w-1 h-1 bg-current rounded-full"></div>
                <div className="w-1 h-1 bg-current rounded-full"></div>
                <div className="w-1 h-1 bg-current rounded-full"></div>
              </div>
            </button>
          </Dropdown>
        </div>
      );
    },
  },
];
